// Foundation tab — the editing side (PR 3, 2026-09-18): add or change a
// register row, assign an unmatched bank credit to a member (and remember its
// reference), maintain the bank-reference and GoCardless donor lookups, and
// the tab's "Refresh now". In-app dialogs only — no browser prompts (law 7).
// Writes go to /api/foundation/* in api/src/functions/foundationEdit.ts, which
// runs app.RebuildFoundation after every change; the caller refetches.
import { useEffect, useMemo, useState } from 'react'

// ---------------------------------------------------------------------------
//  Shared
// ---------------------------------------------------------------------------
export interface EditableMember {
  member_id: number; foundation_ref: string; first_name: string | null; surname: string | null; donorfy_no: number | null
  donorfy_external_key: string | null; email: string | null; status: 'member' | 'not_current'; payment_type: string | null; comment: string | null
}
export interface PickableMember { member_id: number; foundation_ref: string; first_name: string | null; surname: string | null; donorfy_no: number | null; status: string }

async function send(url: string, method: string, body?: unknown): Promise<Record<string, unknown>> {
  const r = await fetch(url, { method, credentials: 'include', headers: body !== undefined ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' }, body: body !== undefined ? JSON.stringify(body) : undefined })
  const j = (await r.json().catch(() => ({}))) as Record<string, unknown>
  if (!r.ok) throw new Error(typeof j.error === 'string' ? j.error : `HTTP ${r.status}`)
  return j
}

const nm = (first: string | null | undefined, surname: string | null | undefined) => [first ?? '', surname ?? ''].join(' ').trim()
const fmtDateTime = (iso: string | null | undefined) => {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
const fmtTime = (iso: string | null | undefined) => {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div className={'modal fnd-modal' + (wide ? ' fnd-modal--wide' : '')} role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="modal__head"><h3 style={{ margin: 0 }}>{title}</h3><button type="button" className="modal__close" aria-label="Close" onClick={onClose}>×</button></div>
        <div className="modal__body">{children}</div>
      </div>
    </div>
  )
}

/** Type-ahead over the register. Loads the register once, on first open. */
export function MemberPicker({ value, onChange, exclude }: { value: number | null; onChange: (m: PickableMember | null) => void; exclude?: number }) {
  const [members, setMembers] = useState<PickableMember[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  useEffect(() => {
    let alive = true
    fetch('/api/foundation/members', { credentials: 'include' })
      .then(async (r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return (await r.json()) as { members: PickableMember[] } })
      .then((j) => { if (alive) setMembers(j.members) })
      .catch((e: unknown) => { if (alive) setError(String((e as Error)?.message ?? e)) })
    return () => { alive = false }
  }, [])
  const needle = q.trim().toLowerCase()
  const shown = useMemo(() => {
    if (!members) return []
    const list = members.filter((m) => m.member_id !== exclude)
    if (!needle) return list.slice(0, 12)
    return list.filter((m) => [m.foundation_ref, m.first_name, m.surname, m.donorfy_no, nm(m.first_name, m.surname)].some((v) => v !== null && v !== undefined && String(v).toLowerCase().includes(needle))).slice(0, 12)
  }, [members, needle, exclude])
  const chosen = members?.find((m) => m.member_id === value) ?? null
  return (
    <div className="fnd-picker">
      <label className="fnd-field"><span>Find a member (number, name or Donorfy number)</span><input type="search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. 323 or Bradley" /></label>
      {error && <p className="fnd-error">{error}</p>}
      {!members && !error && <p className="fnd-note">Loading the register…</p>}
      {chosen && <p className="fnd-note">Chosen: <strong>{chosen.foundation_ref} {nm(chosen.first_name, chosen.surname)}</strong>{chosen.donorfy_no ? ` (Donorfy ${chosen.donorfy_no})` : ''}</p>}
      {members && (
        <div className="fnd-picker-list" role="listbox">
          {shown.map((m) => (
            <button key={m.member_id} type="button" role="option" aria-selected={m.member_id === value} className={'fnd-picker-item' + (m.member_id === value ? ' active' : '')} onClick={() => onChange(m)}>
              <span className="fnd-picker-ref">{m.foundation_ref}</span> {nm(m.first_name, m.surname)}
              <span className="fnd-note"> {m.donorfy_no ? `Donorfy ${m.donorfy_no}` : 'no Donorfy No.'} · {m.status === 'member' ? 'Member' : 'Not current'}</span>
            </button>
          ))}
          {shown.length === 0 && <p className="fnd-empty">No member matches.</p>}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
//  Member add / edit
// ---------------------------------------------------------------------------
export function MemberDialog({ member, defaultStatus, onClose, onSaved }: { member: EditableMember | null; defaultStatus: 'member' | 'not_current'; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({
    foundation_ref: member?.foundation_ref ?? '', first_name: member?.first_name ?? '', surname: member?.surname ?? '',
    donorfy_no: member?.donorfy_no?.toString() ?? '', donorfy_external_key: member?.donorfy_external_key ?? '', email: member?.email ?? '',
    status: member?.status ?? defaultStatus, payment_type: member?.payment_type ?? '', comment: member?.comment ?? '',
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF((s) => ({ ...s, [k]: e.target.value }))
  const save = async () => {
    setBusy(true); setError(null)
    try {
      const body = { ...f, donorfy_no: f.donorfy_no.trim() === '' ? null : Number(f.donorfy_no) }
      if (member) await send(`/api/foundation/members/${member.member_id}`, 'PUT', body)
      else await send('/api/foundation/members', 'POST', body)
      onSaved(); onClose()
    } catch (e) { setError(String((e as Error)?.message ?? e)) } finally { setBusy(false) }
  }
  const remove = async () => {
    if (!member) return
    setBusy(true); setError(null)
    try { await send(`/api/foundation/members/${member.member_id}`, 'DELETE'); onSaved(); onClose() }
    catch (e) { setError(String((e as Error)?.message ?? e)); setConfirmDelete(false) } finally { setBusy(false) }
  }
  return (
    <Modal title={member ? `Edit Foundation ${member.foundation_ref}` : 'Add a member'} onClose={onClose}>
      <div className="fnd-form">
        <label className="fnd-field"><span>Foundation No.</span><input value={f.foundation_ref} onChange={set('foundation_ref')} placeholder="e.g. 468 or 400.1" /></label>
        <label className="fnd-field"><span>Status</span><select value={f.status} onChange={set('status')}><option value="member">Member</option><option value="not_current">Not currently a member</option></select></label>
        <label className="fnd-field"><span>First name</span><input value={f.first_name} onChange={set('first_name')} /></label>
        <label className="fnd-field"><span>Surname</span><input value={f.surname} onChange={set('surname')} /></label>
        <label className="fnd-field"><span>Donorfy No.</span><input value={f.donorfy_no} onChange={set('donorfy_no')} inputMode="numeric" placeholder="constituent number" /></label>
        <label className="fnd-field"><span>Donorfy external key</span><input value={f.donorfy_external_key} onChange={set('donorfy_external_key')} /></label>
        <label className="fnd-field fnd-field--full"><span>Email</span><input value={f.email} onChange={set('email')} type="email" /></label>
        <label className="fnd-field"><span>Payment type</span><input value={f.payment_type} onChange={set('payment_type')} placeholder="FASTER PAYMENTS, Standing Order, DD…" /></label>
        <label className="fnd-field fnd-field--full"><span>Comment</span><textarea value={f.comment} onChange={set('comment')} rows={2} /></label>
      </div>
      <p className="fnd-note">The bank income and GoCardless columns follow the Donorfy number and the bank references; changing the number here re-matches on save.</p>
      {error && <p className="fnd-error">{error}</p>}
      <div className="modal__actions fnd-actions">
        {member && !confirmDelete && <button type="button" className="btn-link btn-link--danger" disabled={busy} onClick={() => setConfirmDelete(true)}>Delete this row</button>}
        {member && confirmDelete && <span><span className="fnd-note">Delete Foundation {member.foundation_ref}? Only possible when no payment, reference or donor points at them. </span><button type="button" className="btn-link btn-link--danger" disabled={busy} onClick={remove}>Yes, delete</button> <button type="button" className="btn-link" onClick={() => setConfirmDelete(false)}>No</button></span>}
        <span className="fnd-spacer" />
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : member ? 'Save changes' : 'Add member'}</button>
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
//  Assign a bank credit
// ---------------------------------------------------------------------------
export interface AssignableRow { bank_txn_id: number; txn_date: string; narrative: string; credit: number | null; reference_key: string | null; member_id: number | null; match_method: string | null; foundation_ref: string | null; first_name: string | null; surname: string | null }

export function AssignDialog({ row, onClose, onDone }: { row: AssignableRow; onClose: () => void; onDone: (note: string | null) => void }) {
  const [memberId, setMemberId] = useState<number | null>(row.member_id)
  const [learn, setLearn] = useState<boolean>(row.member_id === null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' })
  const save = async () => {
    if (memberId === null) { setError('Choose a member first.'); return }
    setBusy(true); setError(null)
    try {
      const j = await send(`/api/foundation/bank/${row.bank_txn_id}/assign`, 'POST', { member_id: memberId, learn })
      const n = Number(j.rows_with_reference ?? 1)
      onDone(learn && row.reference_key ? `Assigned. The reference "${row.reference_key}" is now remembered: ${n} payment${n === 1 ? '' : 's'} carry it.` : 'Assigned this payment only.')
      onClose()
    } catch (e) { setError(String((e as Error)?.message ?? e)) } finally { setBusy(false) }
  }
  const unassign = async () => {
    setBusy(true); setError(null)
    try {
      const j = await send(`/api/foundation/bank/${row.bank_txn_id}/unassign`, 'POST')
      onDone(typeof j.note === 'string' ? j.note : 'Unassigned.')
      onClose()
    } catch (e) { setError(String((e as Error)?.message ?? e)) } finally { setBusy(false) }
  }
  return (
    <Modal title={row.member_id ? 'Change who this payment belongs to' : 'Assign this payment to a member'} onClose={onClose} wide>
      <p className="fnd-note"><strong>{row.txn_date.slice(8, 10)}/{row.txn_date.slice(5, 7)}/{row.txn_date.slice(0, 4)}</strong> · {gbp.format(Number(row.credit ?? 0))} · <span className="fnd-mono">{row.narrative}</span></p>
      <p className="fnd-note">Reference (characters 30 to 54): <span className="fnd-mono">{row.reference_key ?? '(none)'}</span>{row.member_id ? <> · currently {row.foundation_ref} {nm(row.first_name, row.surname)} ({row.match_method === 'manual' ? 'assigned by hand' : 'matched by reference'})</> : <> · not matched</>}</p>
      <MemberPicker value={memberId} onChange={(m) => setMemberId(m ? m.member_id : null)} />
      {row.reference_key && (
        <label className="fnd-check" style={{ marginTop: 8 }}>
          <input type="checkbox" checked={learn} onChange={(e) => setLearn(e.target.checked)} />
          Remember this reference for every payment that carries it (adds or re-points the lookup)
        </label>
      )}
      {error && <p className="fnd-error">{error}</p>}
      <div className="modal__actions fnd-actions">
        {row.member_id && row.match_method === 'manual' && <button type="button" className="btn-link btn-link--danger" disabled={busy} onClick={unassign}>Unassign</button>}
        <span className="fnd-spacer" />
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="btn-primary" onClick={save} disabled={busy || memberId === null}>{busy ? 'Saving…' : 'Assign'}</button>
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
//  Bank reference: re-point or delete
// ---------------------------------------------------------------------------
export function ReferenceDialog({ referenceKey, memberId, uses, onClose, onDone }: { referenceKey: string; memberId: number; uses: number; onClose: () => void; onDone: () => void }) {
  const [chosen, setChosen] = useState<number | null>(memberId)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const save = async () => {
    if (chosen === null) return
    setBusy(true); setError(null)
    try { await send('/api/foundation/references', 'POST', { reference_key: referenceKey, member_id: chosen }); onDone(); onClose() }
    catch (e) { setError(String((e as Error)?.message ?? e)) } finally { setBusy(false) }
  }
  const remove = async () => {
    setBusy(true); setError(null)
    try { await send(`/api/foundation/references?key=${encodeURIComponent(referenceKey)}`, 'DELETE'); onDone(); onClose() }
    catch (e) { setError(String((e as Error)?.message ?? e)) } finally { setBusy(false) }
  }
  return (
    <Modal title="Bank reference" onClose={onClose} wide>
      <p className="fnd-note">Reference <span className="fnd-mono">{referenceKey}</span> currently matches <strong>{uses}</strong> payment{uses === 1 ? '' : 's'}. Re-pointing it moves them all; deleting it leaves them unmatched until assigned again.</p>
      <MemberPicker value={chosen} onChange={(m) => setChosen(m ? m.member_id : null)} />
      {error && <p className="fnd-error">{error}</p>}
      <div className="modal__actions fnd-actions">
        {!confirmDelete && <button type="button" className="btn-link btn-link--danger" disabled={busy} onClick={() => setConfirmDelete(true)}>Delete this reference</button>}
        {confirmDelete && <span><span className="fnd-note">Delete it? </span><button type="button" className="btn-link btn-link--danger" disabled={busy} onClick={remove}>Yes, delete</button> <button type="button" className="btn-link" onClick={() => setConfirmDelete(false)}>No</button></span>}
        <span className="fnd-spacer" />
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="btn-primary" onClick={save} disabled={busy || chosen === null || chosen === memberId}>{busy ? 'Saving…' : 'Re-point'}</button>
      </div>
    </Modal>
  )
}

/** Add a brand-new reference by hand (rare; normally learned from an assignment). */
export function NewReferenceDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [key, setKey] = useState('')
  const [chosen, setChosen] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const save = async () => {
    if (!key.trim() || chosen === null) { setError('A reference and a member are both needed.'); return }
    setBusy(true); setError(null)
    try { await send('/api/foundation/references', 'POST', { reference_key: key.trim(), member_id: chosen }); onDone(); onClose() }
    catch (e) { setError(String((e as Error)?.message ?? e)) } finally { setBusy(false) }
  }
  return (
    <Modal title="Add a bank reference" onClose={onClose} wide>
      <label className="fnd-field fnd-field--full"><span>Reference (characters 30 to 54 of the narrative, as shown on the Bank payments view)</span><input value={key} onChange={(e) => setKey(e.target.value)} maxLength={60} /></label>
      <MemberPicker value={chosen} onChange={(m) => setChosen(m ? m.member_id : null)} />
      {error && <p className="fnd-error">{error}</p>}
      <div className="modal__actions fnd-actions">
        <span className="fnd-spacer" />
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Add reference'}</button>
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
//  GoCardless donor mapping
// ---------------------------------------------------------------------------
export interface EditableDonor { donor_id: number; full_name: string; email: string | null; donorfy_no: number | null; donation_code: string | null }

export function DonorDialog({ donor, codes, onClose, onDone }: { donor: EditableDonor; codes: { code: string; description: string }[]; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ full_name: donor.full_name, email: donor.email ?? '', donorfy_no: donor.donorfy_no?.toString() ?? '', donation_code: donor.donation_code ?? '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const save = async () => {
    setBusy(true); setError(null)
    try {
      await send(`/api/foundation/donors/${donor.donor_id}`, 'PUT', { full_name: f.full_name, email: f.email, donorfy_no: f.donorfy_no.trim() === '' ? null : Number(f.donorfy_no), donation_code: f.donation_code || null })
      onDone(); onClose()
    } catch (e) { setError(String((e as Error)?.message ?? e)) } finally { setBusy(false) }
  }
  return (
    <Modal title={`GoCardless donor: ${donor.full_name}`} onClose={onClose}>
      <div className="fnd-form">
        <label className="fnd-field fnd-field--full"><span>Full name (as GoCardless has it)</span><input value={f.full_name} onChange={(e) => setF((s) => ({ ...s, full_name: e.target.value }))} /></label>
        <label className="fnd-field fnd-field--full"><span>Email (matches the GoCardless customer)</span><input value={f.email} onChange={(e) => setF((s) => ({ ...s, email: e.target.value }))} type="email" /></label>
        <label className="fnd-field"><span>Donorfy No.</span><input value={f.donorfy_no} onChange={(e) => setF((s) => ({ ...s, donorfy_no: e.target.value }))} inputMode="numeric" /></label>
        <label className="fnd-field"><span>Donation code</span>
          <select value={f.donation_code} onChange={(e) => setF((s) => ({ ...s, donation_code: e.target.value }))}>
            <option value="">(use the subscription description)</option>
            {codes.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.description}</option>)}
          </select>
        </label>
      </div>
      <p className="fnd-note">The member is found through the Donorfy number; the donation code you set here wins over the subscription description.</p>
      {error && <p className="fnd-error">{error}</p>}
      <div className="modal__actions fnd-actions">
        <span className="fnd-spacer" />
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
//  Refresh now — the nightly job, on demand, once an hour
// ---------------------------------------------------------------------------
interface RefreshStatus {
  in_flight: boolean
  latest: { id: number; kind?: string; full?: boolean; status: string; requested_by: string; requested_at: string | null; started_at: string | null; completed_at: string | null; message: string | null; duration_ms: number | null } | null
  last_refresh: string | null; last_refresh_status: string | null; lockout_minutes: number; next_available_at: string | null; locked: boolean; can_request: boolean; jobs: string[]
}

export function RefreshBlock({ onCompleted, canFull = false }: { onCompleted: () => void; canFull?: boolean }) {
  const [status, setStatus] = useState<RefreshStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [wasInFlight, setWasInFlight] = useState(false)
  const [full, setFull] = useState(false)
  const load = async () => {
    try {
      const r = await fetch('/api/foundation/refresh', { credentials: 'include' })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const j = (await r.json()) as RefreshStatus
      setStatus(j); setError(null)
      if (wasInFlight && !j.in_flight) { setWasInFlight(false); onCompleted() }
      if (j.in_flight) setWasInFlight(true)
    } catch (e) { setError(String((e as Error)?.message ?? e)) }
  }
  useEffect(() => {
    let alive = true
    const tick = async () => { if (alive) await load() }
    void tick()
    const id = setInterval(tick, status?.in_flight ? 8000 : 60000)
    return () => { alive = false; clearInterval(id) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.in_flight, wasInFlight])
  const request = async () => {
    setBusy(true); setError(null)
    try { await send('/api/foundation/refresh', 'POST', { full: canFull && full }); setWasInFlight(true); await load() }
    catch (e) { setError(String((e as Error)?.message ?? e)) } finally { setBusy(false) }
  }
  const s = status
  const prettyUser = (u: string) => (u.includes('@') ? u.split('@')[0].replace('.', ' ') : u)
  return (
    <div className="fnd-refresh">
      <button type="button" className="btn-secondary" disabled={busy || !s || !s.can_request} onClick={request} title="Pull GoCardless and Donorfy again now and re-match everything. Once an hour.">
        {busy ? 'Requesting…' : s?.in_flight ? 'Refresh in progress…' : full ? 'Reload every GoCardless payment now' : 'Refresh from GoCardless and Donorfy now'}
      </button>
      {canFull && (
      <label className="fnd-check" title="Reports admins only. Historic data does not change. The normal refresh re-pulls only payments charged in the last 90 days, the GoCardless customers, mandates, subscriptions and payouts, Donorfy's Foundation list, and derives the Foundation transactions from the Fundraising tab's Donorfy cache (about two minutes). A full reload re-pulls every GoCardless payment instead (about three minutes).">
        <input type="checkbox" checked={full} disabled={busy || !s || !s.can_request} onChange={(e) => setFull(e.target.checked)} /> Full reload (every GoCardless payment, about 3 minutes; admins only)
      </label>
      )}
      <span className="fnd-status">
        {s?.in_flight && s.latest && <>{s.latest.full ? 'Full reload' : 'Refresh'} in progress — started by {prettyUser(s.latest.requested_by)} at {fmtTime(s.latest.requested_at)}{s.latest.message ? ` — ${s.latest.message}` : ''}. About {s.latest.full ? 3 : 2} minutes; the tab reloads when it finishes. </>}
        {!s?.in_flight && s?.latest && <>Last {s.latest.full ? 'full reload' : 'refresh'} requested by {prettyUser(s.latest.requested_by)} at {fmtDateTime(s.latest.requested_at)}{s.latest.status === 'completed' ? ' — ✓ successful' : s.latest.status === 'error' ? ` — ✗ failed${s.latest.message ? ` (${s.latest.message})` : ''}` : ''}. </>}
        {s && !s.in_flight && (s.locked && s.next_available_at ? <>Available again at {fmtTime(s.next_available_at)}.</> : <>Available to run now.</>)}
        {error && <span className="fnd-error"> {error}</span>}
      </span>
    </div>
  )
}
